import type * as lazyIndex from './lazyIndex';
import { LazyAction, LazyModule, registerLazyOrchestrator } from 'owa-bundling';
import { onPreCoprincipalAccountRemoved } from 'owa-account-source-list-store';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "PersonalOsStore" */ './lazyIndex'),
    {
        name: 'PersonalOsStore',
        initializer: module => () => {
            module.hydrateDevSettings();
        },
    }
);

// Registered eagerly so durable briefing content is removed even if Today never loaded.
registerLazyOrchestrator(
    onPreCoprincipalAccountRemoved,
    lazyModule,
    module => module.clearMyOutlookOnAccountRemovedOrchestrator
);

export const lazyStartMyOutlookSimulation: LazyAction<
    typeof lazyIndex.startMyOutlookSimulation,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.startMyOutlookSimulation);

export const lazyAdvanceMyOutlookSimulation: LazyAction<
    typeof lazyIndex.advanceMyOutlookSimulation,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.advanceMyOutlookSimulation);

export const lazyExitMyOutlookSimulation: LazyAction<
    typeof lazyIndex.exitMyOutlookSimulation,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.exitMyOutlookSimulation);

export const lazyAttachMyOutlook: LazyAction<typeof lazyIndex.attachMyOutlook, typeof lazyModule> =
    new LazyAction(lazyModule, module => module.attachMyOutlook);
export const lazyInitializeVisibleTodayEntry: LazyAction<
    typeof lazyIndex.initializeVisibleTodayEntry,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.initializeVisibleTodayEntry);
export const lazyReadMyOutlookState: LazyAction<
    typeof lazyIndex.readMyOutlookState,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.readMyOutlookState);

export const lazyReadMyOutlookBriefing: LazyAction<
    typeof lazyIndex.readMyOutlookBriefing,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.readMyOutlookBriefing);

export const lazyReadMyOutlookLiveState: LazyAction<
    typeof lazyIndex.readMyOutlookLiveState,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.readMyOutlookLiveState);

export const lazyPrefetchMyOutlookBriefing: LazyAction<
    typeof lazyIndex.prefetchMyOutlookBriefing,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.prefetchMyOutlookBriefing);

export const lazyReadMyOutlookCredits: LazyAction<
    typeof lazyIndex.readMyOutlookCredits,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.readMyOutlookCredits);

export const lazyReadMyOutlookEligibility: LazyAction<
    typeof lazyIndex.readMyOutlookEligibility,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.readMyOutlookEligibility);

export const lazyReadMyOutlookCustomInstructions: LazyAction<
    typeof lazyIndex.readMyOutlookCustomInstructions,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.readMyOutlookCustomInstructions);

export const lazyReplaceMyOutlookCustomInstructions: LazyAction<
    typeof lazyIndex.replaceMyOutlookCustomInstructions,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.replaceMyOutlookCustomInstructions);

export const lazySendBriefingUserAction: LazyAction<
    typeof lazyIndex.sendBriefingUserAction,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.sendBriefingUserAction);

export const lazyPrepareBriefingReply: LazyAction<
    typeof lazyIndex.prepareBriefingReply,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.prepareBriefingReply);

export const lazySendBriefingReply: LazyAction<
    typeof lazyIndex.sendBriefingReply,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.sendBriefingReply);

export const lazyReportBriefingNativeAction: LazyAction<
    typeof lazyIndex.reportBriefingNativeAction,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.reportBriefingNativeAction);

export const lazyInitializeMyOutlookForDebug: LazyAction<
    typeof lazyIndex.initializeMyOutlookForDebug,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.initializeMyOutlookForDebug);

export const lazyRefreshMyOutlookForDebug: LazyAction<
    typeof lazyIndex.refreshMyOutlookForDebug,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.refreshMyOutlookForDebug);

export const lazyRequestBriefingRefresh: LazyAction<
    typeof lazyIndex.requestBriefingRefresh,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.requestBriefingRefresh);

export const lazyShowBriefingUpdate: LazyAction<
    typeof lazyIndex.showBriefingUpdate,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.showBriefingUpdate);

export const lazyLoadBriefingSchedulePreference: LazyAction<
    typeof lazyIndex.loadBriefingSchedulePreference,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.loadBriefingSchedulePreference);

export const lazySaveBriefingSchedulePreference: LazyAction<
    typeof lazyIndex.saveBriefingSchedulePreference,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.saveBriefingSchedulePreference);

export const lazyEnableBriefingExperience: LazyAction<
    typeof lazyIndex.enableBriefingExperience,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.enableBriefingExperience);

export const lazyRestoreTodayDocumentView: LazyAction<
    typeof lazyIndex.restoreTodayDocumentView,
    typeof lazyModule
> = new LazyAction(lazyModule, module => module.restoreTodayDocumentView);
